import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import StatusBadge from "@/components/gate-check/StatusBadge";
import { fmtIST } from "@/lib/datetime";
import { downloadPassJpeg, downloadPassPdf } from "@/lib/passExport";
import { Printer, ArrowLeft, WhatsappLogo, FilePdf, Image } from "@phosphor-icons/react";

export default function VisitorPass() {
    const { id } = useParams();
    const nav = useNavigate();
    const [visit, setVisit] = useState(null);
    const [busyDl, setBusyDl] = useState(null);
    const cardRef = useRef(null);

    useEffect(() => {
        api.get(`/gate-check/visits/${id}`).then((r) => setVisit(r.data));
    }, [id]);

    const doDownload = async (kind) => {
        if (!cardRef.current) return;
        setBusyDl(kind);
        try {
            const fname = `visitor-pass-${id.slice(0, 8)}.${kind}`;
            if (kind === "pdf") await downloadPassPdf(cardRef.current, fname);
            else await downloadPassJpeg(cardRef.current, fname);
        } finally { setBusyDl(null); }
    };

    if (!visit) return <div className="p-10 text-center text-muted-foreground">Loading pass…</div>;

    const photoSrc = visit.photo_base64_inline
        || (visit.photo_path
            ? `${process.env.REACT_APP_BACKEND_URL}/api/photos/${visit.photo_path}?auth=${localStorage.getItem("access_token")}`
            : null);

    const openWhatsApp = () => {
        const msg = encodeURIComponent(
            `Hi ${visit.visitor_name}, here is your visitor pass for BBS Global.\nPass No: ${visit.id.slice(0, 8).toUpperCase()} · Gate: ${visit.gate_name || "Main"}`,
        );
        const digits = (visit.visitor_phone || "").replace(/\D/g, "");
        const target = digits ? `https://wa.me/${digits}?text=${msg}` : `https://wa.me/?text=${msg}`;
        window.open(target, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="min-h-full">
            <div className="print:hidden flex items-center justify-between p-4 border-b border-border">
                <Button variant="outline" className="h-10" onClick={() => nav(-1)} data-testid="btn-back">
                    <ArrowLeft size={16} className="mr-2" /> Back
                </Button>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={openWhatsApp}
                        variant="outline"
                        className="h-10 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="btn-whatsapp-pass"
                    >
                        <WhatsappLogo size={16} className="mr-2" weight="fill" /> WhatsApp
                    </Button>
                    <Button
                        onClick={() => doDownload("pdf")}
                        disabled={busyDl === "pdf"}
                        variant="outline"
                        className="h-10"
                        data-testid="btn-pdf-pass"
                    >
                        <FilePdf size={16} className="mr-2" /> {busyDl === "pdf" ? "…" : "PDF"}
                    </Button>
                    <Button
                        onClick={() => doDownload("jpg")}
                        disabled={busyDl === "jpg"}
                        variant="outline"
                        className="h-10"
                        data-testid="btn-jpg-pass"
                    >
                        <Image size={16} className="mr-2" /> {busyDl === "jpg" ? "…" : "JPG"}
                    </Button>
                    <Button
                        onClick={() => window.print()}
                        data-testid="btn-print-pass"
                        className="h-10 bg-brand hover:bg-brand/90 text-white"
                    >
                        <Printer size={16} className="mr-2" weight="bold" /> Print Pass
                    </Button>
                </div>
            </div>

            <div className="p-6 flex justify-center print:p-0">
                <Card ref={cardRef} className="pass-card w-full max-w-[420px] border-2 border-brand rounded-md overflow-hidden print:border print:shadow-none">
                    <div className="bg-brand text-white p-4 flex items-center justify-between">
                        <div>
                            <div className="overline text-white/80 text-[9px]">BBS Global · Visitor Pass</div>
                            <div className="font-display font-black text-2xl tracking-tighter leading-none mt-1">
                                Visitor<span className="text-white">IQ</span>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-[9px] overline text-white/80">Pass No.</div>
                            <div className="font-mono text-xs">{visit.id.slice(0, 8).toUpperCase()}</div>
                        </div>
                    </div>

                    <div className="p-5 flex gap-4">
                        <div className="w-28 h-32 bg-secondary rounded-md flex items-center justify-center overflow-hidden border border-border">
                            {photoSrc ? (
                                <img src={photoSrc} alt={visit.visitor_name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="text-4xl font-display font-black text-brand">
                                    {visit.visitor_name?.[0]?.toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="overline text-[9px]">Visitor</div>
                            <div className="font-display font-black text-xl tracking-tight leading-tight">
                                {visit.visitor_name}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">{visit.company || "Individual"}</div>
                            <div className="mt-3">
                                <StatusBadge status={visit.status} />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 px-5 pb-5 text-xs">
                        <Info label="Phone" value={visit.visitor_phone} />
                        <Info label="ID" value={visit.id_number || "—"} />
                        <Info label="Host" value={visit.host_name || "—"} />
                        <Info label="Gate" value={visit.gate_name || "—"} />
                        <Info label="Vehicle" value={visit.vehicle_number || "—"} />
                        <Info label="Check-in" value={fmtIST(visit.check_in_at)} />
                    </div>

                    <div className="px-5 pb-4">
                        <div className="overline text-[9px] mb-1">Purpose</div>
                        <div className="text-sm">{visit.purpose}</div>
                    </div>

                    <div className="bg-secondary border-t border-border px-5 py-3 flex items-center justify-between">
                        <div className="text-[10px] text-muted-foreground">
                            Present this pass at exit · Valid for today only
                        </div>
                        <div className="text-[10px] font-mono text-brand font-bold">
                            BBS GLOBAL PVT. LTD.
                        </div>
                    </div>
                </Card>
            </div>
            <style>{`
                @page { size: 110mm 165mm; margin: 4mm; }
                @media print {
                    html, body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .pass-card { max-width: 100mm !important; margin: 0 auto !important; }
                    .pass-card .p-5 { padding: 10px !important; }
                    .pass-card .p-4 { padding: 8px !important; }
                }
            `}</style>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div>
            <div className="overline text-[9px]">{label}</div>
            <div className="font-semibold truncate">{value}</div>
        </div>
    );
}
